import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, SelectPicker, Table } from "rsuite";
import { Button } from "../../components/button.component";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { AppRouteApi } from "../../config/appRoute";
import { useAppDispatch, useAppSelector } from "../../core/redux";
import { ActionsCaregiverTable } from "../../core/redux/slices/sliceCaregiverTable";
import {
  IPaginationSearchFilter,
  useTablePaginationSearchFilter,
} from "../../hooks";
import {
  ApiSchemaCaregiver,
  ApiSchemaFilteredCaregiver,
} from "../../models/api/caregiver";
import { AxiosAuth } from "../../core/axios";
import { FcOk } from "react-icons/fc";
import { ApiResponseContainer } from "../../models/api";
import { UtilityError } from "../../utils";
import { ApiCaregiver } from "../../core/apis";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaCaregiver & {
  name: string;
  phoneNumber: string;
  champsIdNo: string;
};

export function CaregiverDataExchangeTable({
  selectedCompany,
}: {
  selectedCompany: string;
}) {
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, nextPageToken, shouldRefetch } = useAppSelector(
    (state) => state.caregiverTable
  );
  const { data: allCaregiverData, isValidating: isLoadingCaregiverDropdown } =
    ApiCaregiver.GetAllFromCompany.Hook(selectedCompany);

  // State for selected caregiver filter
  const [selectedCaregiver, setSelectedCaregiver] = useState("");

  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      NextPageToken: nextPageToken,
      CompanyId: selectedCompany,
      SearchKey: selectedCaregiver,
    };
  }, [pageNumber, pageSize, nextPageToken, selectedCompany, selectedCaregiver]);
  const { apiData, isFetching, refetch } = useTablePaginationSearchFilter<
    ApiSchemaFilteredCaregiver,
    typeof ActionsCaregiverTable
  >(ActionsCaregiverTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Caregiver.Root(),
  });
  const [isSyncing, setIsSyncing] = useState({ status: false, id: "" });
  const [sortBy, setSortBy] = useState<keyof TableData>("name");
  const [sortAsc, setSortAsc] = useState(true);

  const tableData = useMemo(() => {
    return (apiData?.list || []).map((caregiver) => ({
      ...caregiver,
      name: `${caregiver.firstName} ${caregiver.lastName}`,
      phoneNumber: caregiver.phoneNumber || "",
      champsIdNo: caregiver.champsIdNo || "",
    }));
  }, [apiData?.list]);

  const sortedData = useMemo(() => {
    if (!sortBy) return tableData;
    return [...tableData].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortAsc ? 1 : -1;
      if (!bValue) return sortAsc ? -1 : 1;
      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortAsc ? compareResult : -compareResult;
    });
  }, [tableData, sortBy, sortAsc]);

  const caregiverSelectItems = useMemo(() => {
    return (
      allCaregiverData?.map((caregiver) => ({
        label: `${caregiver.firstName} ${caregiver.lastName}`,
        value: caregiver.firstName,
      })) || []
    );
  }, [allCaregiverData]);

  const handleActivation = async (id: string) => {
    try {
      setIsSyncing({ status: true, id: id });
      await AxiosAuth.post<ApiResponseContainer<any>>(
        AppRouteApi.HouseKeeping.CaregiverById(id)
      );
      refetch();
    } catch (error: any) {
      UtilityError.parseErrorMessage(error, 20000);
    } finally {
      setIsSyncing({ status: false, id: "" });
    }
  };

  const onPageChange = useCallback(
    (value) => dispatch(ActionsCaregiverTable.setEntryCount(value)),
    [dispatch]
  );

  const handleCaregiverChange = (value: string) => {
    setSelectedCaregiver(value);
    dispatch(ActionsCaregiverTable.goToPage(1));
  };

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    return () => {
      dispatch(ActionsCaregiverTable.resetData());
    };
  }, [dispatch]);

  return (
    <div>
      <div className="row g-0 mb-4">
        <div className="col-12 mb-3">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Filters:</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Caregiver</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  data={caregiverSelectItems}
                  value={selectedCaregiver}
                  onChange={(value) => handleCaregiverChange(value as string)}
                  searchable
                  cleanable
                  placeholder="Select Caregiver"
                  block
                  disabled={isLoadingCaregiverDropdown}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center mb-2">
        <div className="d-flex">
          <div className="align-self-center me-2">Show</div>
          <PaginationEntriesInput onChange={onPageChange} />
          <div className="align-self-center ms-2">entries</div>
        </div>
      </div>
      <Table
        autoHeight
        data={sortedData}
        loading={isFetching}
        className=""
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortColumn, sortType) => {
          setSortBy(sortColumn as keyof TableData);
          setSortAsc(sortType === "asc");
        }}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Caregiver Available</h3>
          </div>
        )}
      >
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Caregiver Name">Caregiver Name</span>
          </HeaderCell>
          <Cell dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone Number">Phone Number</span>
          </HeaderCell>
          <Cell dataKey="phoneNumber" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Champ's id no.">Champ's id no.</span>
          </HeaderCell>
          <Cell dataKey="champsIdNo" />
        </Column>
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Last Synced">Last Synced</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaCaregiver) =>
              rowData.isDESynced ? (
                <span className="d-flex align-items-center">
                  <FcOk className="me-2" />
                  {rowData.lastDESyncedTime
                    ? new Date(rowData.lastDESyncedTime).toLocaleString()
                    : "-"}
                </span>
              ) : (
                <Button
                  name="Sync Now"
                  className="btn-primary btn-sm"
                  onClick={() => handleActivation(rowData.sKey)}
                  loading={isSyncing.status && rowData.sKey === isSyncing.id}
                />
              )
            }
          </Cell>
        </Column>
      </Table>

      {sortedData.length > 0 && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={apiData?.totalCount || 0}
            pages={Math.ceil((apiData?.totalCount || 0) / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) => dispatch(ActionsCaregiverTable.goToPage(page))}
          />
        </div>
      )}
    </div>
  );
}
